/**
 * Analytics (Reportes Guardados) READ layer — Oxbo-P.
 *
 * Injected into the shared `pApi` via `injectEndpoints` so the contract files
 * (`src/api/pApi.ts`, `src/api/types.ts`) stay untouched and branches merge
 * cleanly. Every number here (ROP, NPT roll-ups, time-vs-depth, by-depth
 * buckets, end-of-well totals) is SERVER-computed by Oxbo-P; Oxbo-Z never
 * recomputes a KPI — it only formats and lays the values out.
 *
 * All endpoints are well-scoped (by uwi), live, under /v1 with JWT auth:
 *   GET /wells/{well_uwi}/analytics/rop-by-section
 *   GET /wells/{well_uwi}/analytics/npt-by-section
 *   GET /wells/{well_uwi}/analytics/time-vs-depth
 *   GET /wells/{well_uwi}/analytics/end-of-well
 *   GET /wells/{well_uwi}/full-job/by-depth?bucket_size=500
 *
 * Types live here (local), not in the shared contract `types.ts`.
 */
import { pApi } from "@api/pApi";

// ── ROP by section ──────────────────────────────────────────────────────────
export interface RopSectionRow {
  section: string;
  footage: number;
  hours: number;
  /** Server-computed footage/hours. Null when hours are zero / unavailable. */
  rop: number | null;
}
export interface RopBySectionResponse {
  well_uwi: string;
  unit_profile_key: string;
  sections: RopSectionRow[];
}

// ── NPT by section ──────────────────────────────────────────────────────────
export interface NptSectionRow {
  section: string;
  npt_hours: number;
  total_hours: number;
  npt_pct: number | null;
}
export interface NptBySectionResponse {
  well_uwi: string;
  unit_profile_key: string;
  total_npt_hours: number;
  total_hours: number;
  npt_pct: number | null;
  sections: NptSectionRow[];
}

// ── Time vs depth ───────────────────────────────────────────────────────────
export interface TimeDepthPoint {
  day: number;
  md: number | null;
  report_date: string | null;
}
export interface TimeVsDepthResponse {
  well_uwi: string;
  unit_profile_key: string;
  actual: TimeDepthPoint[];
  /** Plan series are not yet served (plan_available=false) — kept nullable so
   * planned/p10/p90 render automatically once Oxbo-P provides them. */
  planned: TimeDepthPoint[] | null;
  p10: TimeDepthPoint[] | null;
  p90: TimeDepthPoint[] | null;
  plan_available: boolean;
  note: string | null;
}

// ── End of well ─────────────────────────────────────────────────────────────
export interface EndOfWellSectionRow {
  section: string;
  footage: number;
  hours: number;
  npt_hours: number;
  npt_pct: number | null;
  rop: number | null;
}
export interface EndOfWellResponse {
  well_uwi: string;
  unit_profile_key: string;
  total_days: number;
  operating_days: number;
  total_footage: number;
  md_final: number | null;
  tvd_final: number | null;
  total_cost: number | null;
  /** ISO code, or "MIXED" when the job spans currencies (no symbol then). */
  currency: string;
  total_hours: number;
  npt_hours: number;
  npt_pct: number | null;
  rop_overall: number | null;
  spud_date: string | null;
  last_report_date: string | null;
  report_count: number;
  sections: EndOfWellSectionRow[];
}

// ── Full job by depth ───────────────────────────────────────────────────────
export interface ByDepthBucket {
  depth_from: number;
  depth_to: number;
  hours: number;
  npt_hours: number;
  footage: number;
  cost: number | null;
  activity_count: number;
}
export interface ByDepthResponse {
  well_uwi: string;
  unit_profile_key: string;
  bucket_size: number;
  md_min: number | null;
  md_max: number | null;
  total_hours: number;
  total_npt_hours: number;
  total_cost: number | null;
  currency: string;
  buckets: ByDepthBucket[];
}

const uwi = (u: string) => encodeURIComponent(u);

export const analyticsApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    getRopBySection: b.query<RopBySectionResponse, string>({
      query: (wellUwi) => `/wells/${uwi(wellUwi)}/analytics/rop-by-section`,
    }),
    getNptBySection: b.query<NptBySectionResponse, string>({
      query: (wellUwi) => `/wells/${uwi(wellUwi)}/analytics/npt-by-section`,
    }),
    getTimeVsDepth: b.query<TimeVsDepthResponse, string>({
      query: (wellUwi) => `/wells/${uwi(wellUwi)}/analytics/time-vs-depth`,
    }),
    getEndOfWell: b.query<EndOfWellResponse, string>({
      query: (wellUwi) => `/wells/${uwi(wellUwi)}/analytics/end-of-well`,
    }),
    getFullJobByDepth: b.query<
      ByDepthResponse,
      { wellUwi: string; bucketSize?: number }
    >({
      query: ({ wellUwi, bucketSize }) => ({
        url: `/wells/${uwi(wellUwi)}/full-job/by-depth`,
        params: { bucket_size: bucketSize ?? 500 },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRopBySectionQuery,
  useGetNptBySectionQuery,
  useGetTimeVsDepthQuery,
  useGetEndOfWellQuery,
  useGetFullJobByDepthQuery,
} = analyticsApi;
